import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';

export interface SeoConfig {
  title: string;
  description: string;
  keywords: string[];
  robots?: string;
  image?: string | null;
  imageWidth?: number | null;
  imageHeight?: number | null;
  type?: 'website' | 'product';
}

const BRAND = 'MilkHouse';
const SHARE_IMAGE_PATH = '/og-image.png';
const SHARE_IMAGE_WIDTH = '1200';
const SHARE_IMAGE_HEIGHT = '630';

export const HOME_TITLE = `${BRAND} — оптова доставка продуктів`;

export const HOME_DESCRIPTION =
  'Оптовий каталог MilkHouse: молочні продукти, бакалія, борошно, м\'ясо, напої, кава, чай і снеки. Ціни, фасування та замовлення з доставкою.';

export const HOME_KEYWORDS = [
  'MilkHouse',
  'оптова доставка продуктів',
  'продукти харчування оптом',
  'молочні продукти оптом',
  'бакалія оптом',
  'борошно оптом',
  'м\'ясо оптом',
  'кава оптом',
  'чай оптом',
  'снеки оптом',
];

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly router = inject(Router);

  setHome(categoryName?: string | null, image?: { url: string; width?: number | null; height?: number | null } | null): void {
    const picture = image?.url || this.shareImageUrl();

    if (categoryName) {
      this.set({
        title: `${categoryName} оптом — ${BRAND}`,
        description: `${categoryName} оптом у каталозі MilkHouse. Оберіть фасування та ціну і додайте товар до замовлення з доставкою.`,
        keywords: [categoryName, `${categoryName} оптом`, ...HOME_KEYWORDS],
        image: picture,
        imageWidth: image?.width,
        imageHeight: image?.height,
      });
      return;
    }

    this.set({
      title: HOME_TITLE,
      description: HOME_DESCRIPTION,
      keywords: HOME_KEYWORDS,
      image: picture,
      imageWidth: image?.width,
      imageHeight: image?.height,
    });
  }

  setProduct(product: {
    name: string;
    sku?: string | null;
    categoryName?: string | null;
    weightLabel?: string | null;
    units?: string | null;
    package?: string | null;
    price?: number | null;
    wholesalePrice?: number | null;
    imageUrl?: string | null;
  }): void {
    const heading = withWeight(product.name, measureLabel(product.weightLabel, product.units));
    const extras = [product.categoryName, product.package ? `упаковка ${product.package}` : null]
      .filter((part): part is string => !!part);
    const price = product.wholesalePrice ?? product.price;
    const priceText = price != null ? ` Ціна ${price.toFixed(2)} грн.` : '';
    const extraText = extras.length ? ` ${extras.join(', ')}.` : '';
    const description = trimDescription(`${heading}.${extraText}${priceText} Оптове замовлення з доставкою в MilkHouse.`);

    this.set({
      title: `${heading} — ${BRAND}`,
      description,
      keywords: [
        product.name,
        product.weightLabel ?? '',
        product.units ?? '',
        product.categoryName ? `${product.categoryName} оптом` : '',
        product.sku ?? '',
        'купити оптом',
        ...HOME_KEYWORDS,
      ],
      image: product.imageUrl,
      type: 'product',
    });
  }

  setCart(): void {
    this.set({
      title: `Кошик — ${BRAND}`,
      description: 'Оформлення оптового замовлення MilkHouse. Перевірте товари, кількість і контакти для доставки.',
      keywords: ['кошик', 'оптове замовлення', ...HOME_KEYWORDS],
      robots: 'noindex, nofollow',
    });
  }

  setNotFound(): void {
    this.set({
      title: `Товар не знайдено — ${BRAND}`,
      description: 'Цього товару немає в каталозі MilkHouse. Поверніться до оптового каталогу та оберіть іншу позицію.',
      keywords: HOME_KEYWORDS,
      robots: 'noindex, follow',
    });
  }

  private set(config: SeoConfig): void {
    const description = config.description.trim();
    const keywords = [...new Set(config.keywords.map((k) => String(k).trim()).filter(Boolean))].join(', ');

    this.title.setTitle(config.title);
    this.upsert('name', 'description', description);
    this.upsert('name', 'keywords', keywords);
    this.upsert('name', 'robots', config.robots ?? 'index, follow');
    this.upsert('property', 'og:title', config.title);
    this.upsert('property', 'og:description', description);
    this.upsert('property', 'og:type', config.type ?? 'website');
    this.upsert('property', 'og:locale', 'uk_UA');
    this.upsert('property', 'og:site_name', BRAND);
    this.upsert('property', 'og:url', this.pageUrl());
    this.upsert('name', 'twitter:card', 'summary_large_image');
    this.upsert('name', 'twitter:title', config.title);
    this.upsert('name', 'twitter:description', description);

    const image = this.shareImage(config.image, config.title, config.imageWidth, config.imageHeight);
    this.upsert('property', 'og:image', image.url);
    this.upsert('property', 'og:image:secure_url', image.url);
    this.upsert('property', 'og:image:alt', image.alt);
    this.upsert('name', 'twitter:image', image.url);
    this.upsert('name', 'twitter:image:alt', image.alt);
    if (image.width && image.height) {
      this.upsert('property', 'og:image:type', image.type ?? 'image/png');
      this.upsert('property', 'og:image:width', image.width);
      this.upsert('property', 'og:image:height', image.height);
    } else {
      this.meta.removeTag('property="og:image:type"');
      this.meta.removeTag('property="og:image:width"');
      this.meta.removeTag('property="og:image:height"');
    }
  }

  private pageUrl(): string {
    const path = this.router.url.split('#')[0] || '/';
    const base = environment.siteUrl.replace(/\/$/, '');
    return path.startsWith('/') ? `${base}${path}` : `${base}/${path}`;
  }

  private shareImageUrl(): string {
    return `${environment.siteUrl.replace(/\/$/, '')}${SHARE_IMAGE_PATH}`;
  }

  private shareImage(
    image: string | null | undefined,
    alt: string,
    width?: number | null,
    height?: number | null,
  ): {
    url: string;
    alt: string;
    type?: string;
    width?: string;
    height?: string;
  } {
    const shareUrl = this.shareImageUrl();
    if (image && /^https?:\/\//.test(image) && image !== shareUrl) {
      return {
        url: image,
        alt,
        type: imageType(image),
        width: width ? String(width) : undefined,
        height: height ? String(height) : undefined,
      };
    }
    return {
      url: shareUrl,
      alt: HOME_TITLE,
      type: 'image/png',
      width: SHARE_IMAGE_WIDTH,
      height: SHARE_IMAGE_HEIGHT,
    };
  }

  private upsert(attr: 'name' | 'property', key: string, content: string): void {
    this.meta.updateTag({ [attr]: key, content });
  }
}

function imageType(url: string): string | undefined {
  const file = url.split('?')[0].split('/').pop() ?? '';
  const ext = file.includes('.') ? file.split('.').pop()!.toLowerCase() : '';
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'png') return 'image/png';
  if (ext === 'gif') return 'image/gif';
  return undefined;
}

function measureLabel(weightLabel: string | null | undefined, units: string | null | undefined): string | null {
  const weight = weightLabel?.trim() ?? '';
  const unit = units?.trim() ?? '';
  if (!weight && !unit) return null;
  if (weight && unit && !weight.toLowerCase().includes(unit.toLowerCase())) {
    return `${weight} ${unit}`;
  }
  return weight || unit;
}

function withWeight(name: string, weightLabel: string | null | undefined): string {
  const weight = weightLabel?.trim();
  if (!weight) return name;
  const compact = (value: string) => value.toLowerCase().replace(/\s+/g, '');
  if (compact(name).includes(compact(weight))) return name;
  return `${name}, ${weight}`;
}

function trimDescription(text: string): string {
  const limit = 170;
  if (text.length <= limit) return text;
  return `${text.slice(0, limit - 1).trimEnd()}…`;
}
